/**
 * @fileOverview
 * @name AllOperationHistoriesQuery.ts
 * @license
 */

import { GettableQuery } from './interface';
import {
  OperationHistoriesWithV2,
  OperationHistoryV2,
} from '@c/domain/entities/OperationHistoriesV2';
import { ENV } from '@c/config';
import {
  getDocs,
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  Timestamp,
} from 'firebase/firestore';

export type AllOperationHistoriesGettableQuery = GettableQuery<
  { tenantId: string; startAfterCreatedAt?: Date },
  OperationHistoriesWithV2
>;

export class AllOperationHistoriesQuery implements AllOperationHistoriesGettableQuery {
  public constructor() {}

  public async get({
    tenantId,
    startAfterCreatedAt,
  }: Parameters<AllOperationHistoriesGettableQuery['get']>[0]): ReturnType<
    AllOperationHistoriesGettableQuery['get']
  > {
    const db = getFirestore();

    // V2データのみを取得（全プロジェクト共通）
    const baseQuery = query(
      collection(db, ENV, tenantId, 'operation_histories'),
      orderBy('created_at', 'desc'),
      limit(1000)
    );

    const q = startAfterCreatedAt
      ? query(baseQuery, startAfter(Timestamp.fromDate(startAfterCreatedAt)))
      : baseQuery;

    const snapshot = await getDocs(q);

    const v2Data = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        operatorId: data['operator_id'],
        tenantId: data['tenant_id'],
        operatorVisibility: data['operator_visibility'],
        subject: data['subject'],
        env: data['env'],
        operationData:
          'operation_data' in data
            ? {
                systemMessageType:
                  'system_message_type' in data['operation_data']
                    ? data['operation_data']['system_message_type']
                    : '',
                displayName:
                  'display_name' in data['operation_data']
                    ? data['operation_data']['display_name']
                    : '',
                newDisplayName:
                  'new_display_name' in data['operation_data']
                    ? data['operation_data']['new_display_name']
                    : '',
                oldDisplayName:
                  'old_display_name' in data['operation_data']
                    ? data['operation_data']['old_display_name']
                    : '',
                lineSetting:
                  'line_setting' in data['operation_data']
                    ? data['operation_data']['line_setting']
                    : '',
              }
            : {
                systemMessageType: '',
                displayName: '',
              },
        createdAt: (data['created_at'] as Timestamp).toDate(),
      } as OperationHistoryV2;
    });

    return v2Data as OperationHistoriesWithV2;
  }
}
